import * as THREE from "three";
import { Player } from "../entities/Player.js";
import { ObstacleManager } from "../entities/ObstacleManager.js";
import { BlockManager } from "../entities/BlockManager.js";
import { LaserManager } from "../entities/LaserManager.js";
import { LevelGenerator } from "../entities/LevelGenerator.js";
import { CollisionDetector } from "../utils/CollisionDetector.js";
import { DebugManager } from "../utils/DebugManager.js";
import { BloodParticleSystem } from "../utils/BloodParticleSystem.js";
import { AnalyticsManager } from "../utils/AnalyticsManager.js";
import { PerformanceTracker } from "../utils/PerformanceTracker.js";
import { FrameProfiler } from "../utils/FrameProfiler.js";
import { AudioManager } from "../utils/AudioManager.js";
import { ProjectileManager } from "../entities/ProjectileManager.js";
import {
  fetchChallenge,
  submitScore,
  getLeaderboard,
} from "../utils/SupabaseService.js"; // Import Supabase functions
import { getPlayerName } from "../utils/playerName.js";
import { isNonLeaderboardMapId } from "../levels/customMaps.js";

export class Game {
  constructor(container, scoreElement, gameOverElement, options = {}) {
    this.container = container;
    this.scoreElement = scoreElement;
    this.gameOverElement = gameOverElement;

    // Which map (template bundle) to play, e.g. from a ?map= URL param. null
    // lets the LevelGenerator fall back to the default map. Reading the param
    // itself stays in main.js — Game only receives the resolved option.
    this.mapId = options.mapId || null;

    // Remove direct reference to coordinates element
    // this.coordinatesElement = document.getElementById("coordinates");

    // Game state
    this.isRunning = false;
    this.isDead = false;
    this.hasWon = false; // Designed maps only: set when the finish line is crossed
    this.isPaused = false;
    this.waitingForStart = true; // Add new state for waiting for player interaction
    this.distance = 0; // Distance traveled in world units
    this.gameTime = 0; // Time in milliseconds
    this.gameSpeed = 1.0;
    this.lastFrameTime = 0;
    this.finalScore = 0; // Add a property to store the final score

    // Add callback for score submission result
    this.onScoreSubmitted = null;
    this.onScoreSubmitFailed = null; // Add callback for failure
    this.onStart = null; // Called when a run begins; main.js uses it for the map-title card

    // Initialize performance tracker and analytics manager
    this.performanceTracker = new PerformanceTracker();
    this.frameProfiler = new FrameProfiler();
    this.analytics = new AnalyticsManager();

    // Track distance milestones (in meters)
    this.lastMilestone = 0;
    this.milestoneIncrement = 100; // Track every 100 meters

    // Camera settings
    this.cameraOffsetX = 6; // Position player on left side

    // Fixed size for gameplay area (based on 50px grid cells)
    this.baseFrustumHeight = 10; // Base height (20 grid cells at 0.5 units per cell)
    this.baseFrustumWidth = 16; // Base width for 16:9 aspect ratio
    this.maxVisibleWidth = 19.2; // Max visible width (1920px at 100px per unit)

    // Reference sizes for scaling
    this.gridCellSize = 0.5; // Size of one grid cell in world units
    this.referenceHeight = 1000; // Reference height in pixels (20 grid cells at 50px)
    this.gridHeight = 20; // Standard grid height in cells

    // Initialize audio manager
    this.audioManager = new AudioManager();

    // Initialize Three.js components - change the order
    this.initScene();
    this.initCamera();
    this.initRenderer();

    // Now call resize handler after all components are initialized
    this.handleResize();

    // Initialize debug manager
    this.debugManager = new DebugManager(this.scene);
    // Press 'o' (while the debug overlay is on) to dump a profiler snapshot.
    this.debugManager.onDumpProfile = () => this.frameProfiler.logSummary();
    // Press 'k' (while the debug overlay is on) to A/B the shadow-pass cost.
    this.debugManager.onToggleShadows = () => this.toggleShadows();

    // Initialize game entities
    // Create managers first
    this.blockManager = new BlockManager(this.scene);
    this.obstacleManager = new ObstacleManager(this.scene, this.blockManager);
    this.laserManager = new LaserManager(this.scene, this.blockManager);
    this.projectileManager = new ProjectileManager(
      this.scene,
      this.blockManager
    );

    // Create level generator after managers
    this.levelGenerator = new LevelGenerator(
      this.scene,
      this.blockManager,
      this.obstacleManager,
      this.laserManager,
      this.debugManager,
      this.projectileManager,
      this.mapId
    );

    // Resolved display name of the active map (e.g. "Meltdown"), surfaced for
    // the title card the DOM layer shows at the start of a run.
    this.mapName = this.levelGenerator.map.name;

    // Create player
    this.player = new Player(this.scene, this.blockManager);
    this.collisionDetector = new CollisionDetector();

    // Initialize blood particle system
    this.bloodParticleSystem = new BloodParticleSystem(
      this.scene,
      this.blockManager,
      this.obstacleManager
    );

    // Initially hide the player
    if (this.player.mesh) this.player.mesh.visible = false;

    // Set initial background plane position to match player starting position
    this.backgroundPlane.position.x = this.player.position.x;

    // Initialize the level
    this.levelGenerator.reset();

    // Set up resize handler
    window.addEventListener("resize", this.handleResize.bind(this));

    // Track the last position where we updated the light target matrix
    this.lastLightTargetX = 0;
  }

  /**
   * Show the pause screen with appropriate UI updates
   */
  showPauseScreen() {
    const splashScreen = document.getElementById("splash-screen");
    const pausedIndicator = document.getElementById("paused-indicator");
    const startBtn = document.getElementById("start-button");
    const startHint = document.querySelector(".start-hint");

    if (splashScreen) {
      splashScreen.classList.remove("hidden");
      splashScreen.classList.add("paused");
    }

    if (pausedIndicator) {
      pausedIndicator.classList.remove("hidden");
      pausedIndicator.classList.add("block");
    }

    if (startBtn) {
      startBtn.textContent = "Resume";
    }

    if (startHint) {
      startHint.textContent = "Press Enter to resume";
    }
  }

  /**
   * Hide the pause screen with appropriate UI updates
   */
  hidePauseScreen() {
    const splashScreen = document.getElementById("splash-screen");
    const pausedIndicator = document.getElementById("paused-indicator");
    const startBtn = document.getElementById("start-button");
    const startHint = document.querySelector(".start-hint");

    if (startBtn) {
      startBtn.textContent = "Start";
    }

    if (startHint) {
      startHint.textContent = "Press Enter to start";
    }

    if (pausedIndicator) {
      pausedIndicator.classList.remove("block");
      pausedIndicator.classList.add("hidden");
    }

    if (splashScreen) {
      splashScreen.classList.add("hidden");
      splashScreen.classList.remove("paused");
    }
  }

  initScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x87ceeb); // Sky blue

    // Add background plane to receive shadows
    // Make the plane even larger to ensure it covers the full game area
    const bgGeometry = new THREE.PlaneGeometry(3000, 1000);
    const bgMaterial = new THREE.MeshStandardMaterial({
      color: 0xc0e8ff, // Light blue
      roughness: 1.0,
      metalness: 0.0,
    });
    this.backgroundPlane = new THREE.Mesh(bgGeometry, bgMaterial);
    // Position background plane behind all objects
    // Starting at the player's position rather than at 0
    this.backgroundPlane.position.set(0, 0, -0.5);
    this.backgroundPlane.receiveShadow = true;
    this.scene.add(this.backgroundPlane);

    // Add lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    // Store directional light as class property so we can update its position
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    // Position light to create shadows that come more from the right side
    // Lower X, higher Y, and higher Z values create more right-to-left shadows
    this.directionalLight.position.set(2, 2, 6);
    this.directionalLight.castShadow = true;

    // Set up the directional light target
    this.directionalLight.target = new THREE.Object3D();
    this.directionalLight.target.position.set(0, 0, 0);
    this.scene.add(this.directionalLight.target);

    // Configure shadow properties for better performance
    // Reduced shadow map resolution from 2048 to 1024
    this.directionalLight.shadow.mapSize.width = 1024;
    this.directionalLight.shadow.mapSize.height = 1024;
    this.directionalLight.shadow.camera.near = 0.5;
    this.directionalLight.shadow.camera.far = 20;

    // Adjust shadow camera frustum to better match our game's viewport
    // and accommodate the more angled shadows
    this.directionalLight.shadow.camera.left = -12;
    this.directionalLight.shadow.camera.right = 20;
    this.directionalLight.shadow.camera.top = 8;
    this.directionalLight.shadow.camera.bottom = -8;

    // Adjust shadow bias to prevent shadow artifacts with the new angle
    this.directionalLight.shadow.bias = -0.0001;

    // Add shadow blur for softer, more natural looking shadows
    // Reduced radius from 1.5 to 1.0 for better performance
    this.directionalLight.shadow.radius = 1.0;

    this.scene.add(this.directionalLight);
  }

  initCamera() {
    // Create camera with standard dimensions (will be adjusted in resize handler)
    this.camera = new THREE.OrthographicCamera(
      this.baseFrustumWidth / -2, // left
      this.baseFrustumWidth / 2, // right
      this.baseFrustumHeight / 2, // top
      this.baseFrustumHeight / -2, // bottom
      0.1, // near
      1000 // far
    );

    // Initial camera position
    this.camera.position.set(this.cameraOffsetX, 0, 10);
    this.camera.lookAt(this.cameraOffsetX, 0, 0);

    // Initialize audio manager with camera
    this.audioManager.init(this.camera);
  }

  initRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance", // Request high-performance GPU mode
    });

    // Get container dimensions (respects the aspect ratio set in CSS)
    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;

    this.renderer.setSize(containerWidth, containerHeight);
    this.renderer.shadowMap.enabled = true;
    // Changed from PCFSoftShadowMap to PCFShadowMap for better performance
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace; // Ensure proper color space handling
    this.container.appendChild(this.renderer.domElement);
  }

  // Initialize the game but don't start the animation loop
  init() {
    // Cancel any existing animation frame
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.isRunning = false;
    this.isDead = false;
    this.waitingForStart = true;

    // Hide score element when splash screen is showing
    const scoreElement = document.getElementById("score");
    if (scoreElement) {
      scoreElement.classList.add("hidden");
    }

    // Check for initial position from debug manager
    if (this.debugManager && this.debugManager.initialPlayerPosition > 0) {
      this.distance = this.debugManager.initialPlayerPosition;
      // Also set the player's position
      this.player.position.x = this.distance;
      this.player.mesh.position.x = this.distance;

      // Update camera position
      this.camera.position.x = this.player.position.x + this.cameraOffsetX;

      // Update background plane position
      this.backgroundPlane.position.x = this.player.position.x;

      // Initialize lastLightTargetX to match the player's initial position
      this.lastLightTargetX = this.player.position.x;

      console.log(`Starting at distance: ${this.distance}`);
    } else {
      this.distance = 0;
      // Initialize lastLightTargetX to 0
      this.lastLightTargetX = 0;
    }

    this.gameTime = 0;
    this.gameSpeed = 1.0;
    this.performanceTracker.reset();
    this.lastFrameTime = performance.now();
    this.updateDisplay();

    // Render a single frame to show the initial state
    this.renderer.render(this.scene, this.camera);
  }

  start() {
    // Set game state to running
    this.isRunning = true;
    this.isPaused = false;
    this.waitingForStart = false;

    // Notify the DOM layer that a run is beginning (drives the map-title card).
    if (this.onStart) this.onStart();

    // Hide the "press to start" message if it exists
    const pressToStartEl = document.getElementById("press-to-start");
    if (pressToStartEl) {
      pressToStartEl.classList.add("hidden");
    }

    // Show the player
    if (this.player && this.player.mesh) {
      this.player.mesh.visible = true;
    }

    // Show the player's eyes now that the game is starting
    if (this.player && typeof this.player.showEyes === "function") {
      this.player.showEyes();
    }

    // Reset score display to show actual game stats
    const scoreElement = document.getElementById("score");
    const trackDisplay = document.getElementById("track-display");

    if (scoreElement) {
      // Reset the content to show actual game stats
      scoreElement.innerHTML = `Distance: 0 m<br />Time: 00:00.00`;
      scoreElement.classList.remove("hidden");
    }

    if (trackDisplay) {
      // Track display will be updated by updateDisplay method
      trackDisplay.classList.remove("hidden");
    }

    // Fetch a fresh challenge token when the game actually starts
    console.log("Fetching challenge before starting game...");
    fetchChallenge();

    // Start tracking performance
    this.analytics.enableFrameRateTracking();

    // Track game start event
    this.analytics.trackGameStart();

    // Reset the last frame time
    this.lastFrameTime = performance.now();

    // Only start a new animation loop if we don't already have one running
    if (!this.animationFrameId) {
      this.animate(this.lastFrameTime);
    }
  }

  // New method to show game elements when splash screen is dismissed
  // but before any player interaction
  prepareGameAfterSplashScreen() {
    // Show the player
    if (this.player && this.player.mesh) {
      this.player.mesh.visible = true;

      // Show player's eyes
      if (typeof this.player.showEyes === "function") {
        this.player.showEyes();
      }
    }

    // Use the score element for start instructions
    const scoreElement = document.getElementById("score");

    if (scoreElement) {
      if (window.matchMedia("(max-width: 768px)").matches) {
        // Mobile instructions
        scoreElement.innerHTML = "Tap anywhere to start";
      } else {
        // Desktop instructions
        scoreElement.innerHTML = "Press any key to start";
      }
      scoreElement.classList.remove("hidden");
    }

    // Update track display if music is playing
    this.updateTrackDisplay();

    // Hide the separate press-to-start message if it exists
    const pressToStartEl = document.getElementById("press-to-start");
    if (pressToStartEl) {
      pressToStartEl.classList.add("hidden");
    }
  }

  pause() {
    if (this.isRunning && !this.isDead) {
      this.isPaused = true;
      // Remove audio pause - music should continue playing
    }
  }

  resume() {
    if (this.isPaused) {
      this.isPaused = false;
      // Hide the pause screen
      this.hidePauseScreen();
      // Remove audio resume - music should continue playing
    }
  }

  togglePause() {
    if (this.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
    return this.isPaused;
  }

  restart() {
    // Clear game over screen if visible
    if (this.gameOverElement) {
      this.gameOverElement.classList.add("hidden");
      this.gameOverElement.classList.remove("visible");
    }

    // Use the score element for start instructions
    const scoreElement = document.getElementById("score");
    const trackDisplay = document.getElementById("track-display");

    if (scoreElement) {
      if (window.matchMedia("(max-width: 768px)").matches) {
        // Mobile instructions
        scoreElement.innerHTML = "Tap anywhere to start";
      } else {
        // Desktop instructions
        scoreElement.innerHTML = "Press any key to start";
      }
      scoreElement.classList.remove("hidden");
    }

    // Hide the separate press-to-start message if it exists
    const pressToStartEl = document.getElementById("press-to-start");
    if (pressToStartEl) {
      pressToStartEl.classList.add("hidden");
    }

    // IMPORTANT: Cancel the existing animation frame BEFORE resetting entities
    // This prevents multiple animation loops from running simultaneously
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Reset game entities
    this.player.reset();

    // Important: Reset order matters for dependent systems!
    // Reset ProjectileManager and other entity managers before LevelGenerator
    if (this.projectileManager) this.projectileManager.reset();
    this.obstacleManager.reset();
    this.laserManager.reset();
    this.blockManager.reset();

    // Reset level generator after all entity managers are reset
    this.levelGenerator.reset();

    this.performanceTracker.reset();
    this.bloodParticleSystem.clearParticles();
    if (this.debugManager) {
      this.debugManager.clearMarkers();
    }

    // Show the player
    if (this.player && this.player.mesh) {
      this.player.mesh.visible = true;
      if (typeof this.player.showEyes === "function") {
        this.player.showEyes();
      }
    }

    if (this.debugManager && this.debugManager.initialPlayerPosition > 0) {
      this.distance = this.debugManager.initialPlayerPosition;
    } else {
      this.distance = 0;
    }

    this.player.position.x = this.distance;
    this.player.mesh.position.x = this.distance;
    this.camera.position.x = this.player.position.x + this.cameraOffsetX;

    // Update light target and background plane position
    this.backgroundPlane.position.x = this.player.position.x;
    this.directionalLight.position.x = this.player.position.x + 2;
    this.directionalLight.target.position.x = this.player.position.x;
    this.directionalLight.target.updateMatrixWorld();

    // Update the lastLightTargetX to match current player position
    this.lastLightTargetX = this.player.position.x;

    this.isRunning = true;
    this.isDead = false;
    this.hasWon = false; // clear any prior course completion
    this.waitingForStart = true; // Wait for player interaction after restart
    this.gameTime = 0;
    this.gameSpeed = 1.0;
    this.lastMilestone = 0;

    // Reset the last frame time to now
    this.lastFrameTime = performance.now();

    this.updateDisplay();

    // Only start a new animation loop if we don't already have one running
    if (!this.animationFrameId) {
      this.animate(this.lastFrameTime);
    }
  }

  // Calculate the player's score based on distance and time
  calculateScore(distance, timeMs) {
    // Convert distance to meters for scoring (rounded to integer)
    const distanceInMeters = Math.floor(distance * 10);

    // Base score is from distance (10 points per meter)
    const distanceScore = distanceInMeters * 10;

    // Time penalty: 1 second = 100 points penalty (or 0.1 points per millisecond)
    // Round to match the display precision (hundredths of a second)
    const timePenalty = Math.floor(timeMs / 10); // 1 point per 10ms

    // Calculate final score: distance points minus time penalty
    const finalScore = Math.max(0, distanceScore - timePenalty);

    return {
      score: finalScore,
      distanceScore: distanceScore,
      timePenalty: timePenalty,
    };
  }

  gameOver(reason = "obstacle") {
    this.isRunning = false;
    this.isDead = true;

    // Capture the player's exact end position up front. die() only hides the
    // mesh (it doesn't move the player), but reading it here makes this
    // unambiguously "the position the run ended at".
    const endPositionX = this.player.position.x;
    const endPositionY = this.player.position.y;

    // Get the player's position and trigger death animation
    const deathPosition = this.player.die();

    // Trigger blood particle effect at player's death position
    // For falling deaths, ensure blood appears at bottom of screen
    if (reason === "fall") {
      // Position blood at the bottom of the camera view
      const bottomPosition = {
        x: deathPosition.x,
        y: this.camera.position.y + this.camera.bottom - 0.5,
        z: deathPosition.z,
      };
      this.bloodParticleSystem.createParticles(bottomPosition);
    } else {
      this.bloodParticleSystem.createParticles(deathPosition);
    }

    // Update game over message based on reason, but use same color for all
    let gameOverMessage = `<span class="death-text">DEAD!</span>`;
    let deathDescription = "";

    if (reason === "fall") {
      deathDescription = `<span class="death-text">You fell to your doom.</span>`;
    } else if (reason === "obstacle") {
      deathDescription = `<span class="death-text">You were impaled. Ouch!</span>`;
    } else if (reason === "laser") {
      deathDescription = `<span class="death-text">You were zapped into oblivion.</span>`;
    } else if (reason === "projectile") {
      // Assuming projectile death reason exists
      deathDescription = `<span class="death-text">Annihilated by projectile.</span>`;
    }

    // Format the final distance and time for display
    const formattedDistance = this.formatDistance(this.distance);
    const formattedTime = this.formatTime(this.gameTime);

    // Calculate the final score details
    const scoreResult = this.calculateScore(this.distance, this.gameTime);
    this.finalScore = scoreResult.score;

    // The map segment (scene/template name) the run ended in, plus the segment
    // just before it (null when the run ended in the very first segment). Look
    // up the player's actual end position, not this.distance (a collision-frozen
    // high-water mark that can sit ahead of the player after a backward
    // wall-jump), so the segment is consistent with end_position_x.
    const { current: sceneName, previous: previousSceneName } =
      this.levelGenerator.getSceneContextAtPosition(endPositionX);

    // Descriptive end-of-run stats, persisted alongside the score (runs table).
    const runStats = {
      outcome: "died",
      deathCause: reason, // obstacle | fall | laser | projectile
      mapSegment: sceneName,
      previousMapSegment: previousSceneName,
      endPositionX,
      endPositionY,
      distanceM: Math.floor(this.distance * 10),
    };

    // Prepare the game over display content
    document.getElementById("game-over-message").innerHTML = gameOverMessage;
    document.getElementById("game-over-stats").innerHTML = `
      <div class="death-description">${deathDescription}</div>
      <div class="final-score"><strong>Score: ${this.finalScore}</strong></div>
      <div class="stat-row">Distance: ${formattedDistance} <span class="score-bonus">(+${scoreResult.distanceScore} pts)</span></div>
      <div class="stat-row">Time: ${formattedTime} <span class="score-penalty">(-${scoreResult.timePenalty} pts)</span></div>
    `;

    // Add a 1-second delay before showing the game over screen
    setTimeout(async () => {
      // Make the timeout callback async
      // Remove hidden class and add visible class
      this.gameOverElement.classList.remove("hidden");
      this.gameOverElement.classList.add("visible");

      // Submit score after showing game over screen
      // Check score > 0 before attempting submission
      if (this.finalScore > 0) {
        try {
          console.log("Calling handleScoreSubmission...");
          const submissionResult = await this.handleScoreSubmission(runStats); // Await the result

          if (submissionResult && submissionResult.success) {
            console.log(
              "Score submitted successfully from Game.js!",
              submissionResult.message
            );
            // Trigger the success callback if it exists
            if (this.onScoreSubmitted) {
              this.onScoreSubmitted(submissionResult); // Pass the whole result object
            }
          } else {
            console.error(
              "Score submission failed from Game.js:",
              submissionResult?.message || "Unknown error"
            );
            // Trigger the failure callback if it exists
            if (this.onScoreSubmitFailed) {
              this.onScoreSubmitFailed(
                submissionResult?.message || "Unknown error"
              );
            }
          }
        } catch (error) {
          console.error("Error during handleScoreSubmission call:", error);
          if (this.onScoreSubmitFailed) {
            this.onScoreSubmitFailed(
              error.message || "Exception during submission"
            );
          }
        }
      } else {
        console.log("Score is 0, skipping submission.");
        // Optionally trigger a specific callback or state for non-submission
        if (this.onScoreSubmitFailed) {
          // Reuse failure callback to hide name change section
          this.onScoreSubmitFailed("Score was 0.");
        }
      }
    }, 1000); // Keep 1 second delay

    // Track game over event with current scene name (computed above for runStats)
    this.analytics.trackGameOver(
      reason,
      this.distance,
      this.gameTime,
      sceneName,
      this.finalScore
    );
  }

  // Completing a designed (finite) course: the player crossed the finish line
  // alive. Mirrors gameOver()'s end-of-run flow (final score, results screen,
  // score submission) but with a win presentation instead of a death.
  win() {
    this.isRunning = false;
    this.hasWon = true;
    this.isDead = false; // a win is not a death — no death animation or blood

    // The player's exact position as they crossed the finish line — captured
    // before we overwrite this.distance with the fixed course length below.
    const endPositionX = this.player.position.x;
    const endPositionY = this.player.position.y;

    // Pin distance to the fixed course length so the shared distance-minus-time
    // score becomes a time trial: same course length for everyone, faster runs
    // pay a smaller time penalty and rank higher under the existing `score DESC`.
    this.distance = this.levelGenerator.getCourseLength();

    const formattedTime = this.formatTime(this.gameTime);
    const scoreResult = this.calculateScore(this.distance, this.gameTime);
    this.finalScore = scoreResult.score;

    // Descriptive end-of-run stats (a completion). mapSegment mirrors the
    // analytics "finish" scene; distanceM is the credited course length. The
    // finish line sits on the last segment's right edge, so look up the context
    // at the crossing position to get the segment just before the finish.
    const runStats = {
      outcome: "finished",
      deathCause: null, // no hazard killed the player — they finished
      mapSegment: this.levelGenerator.map.finishSceneName ?? null,
      previousMapSegment:
        this.levelGenerator.getSceneContextAtPosition(endPositionX).previous,
      endPositionX,
      endPositionY,
      distanceM: Math.floor(this.distance * 10),
    };

    // Reuse the game-over dialog DOM with completion copy.
    document.getElementById("game-over-message").innerHTML =
      `<span class="win-text">FINISH!</span>`;
    document.getElementById("game-over-stats").innerHTML = `
      <div class="death-description">You completed ${this.mapName}!</div>
      <div class="final-score"><strong>Score: ${this.finalScore}</strong></div>
      <div class="stat-row">Time: ${formattedTime} <span class="score-penalty">(-${scoreResult.timePenalty} pts)</span></div>
    `;

    // Same 1s delay + submission flow as gameOver().
    setTimeout(async () => {
      this.gameOverElement.classList.remove("hidden");
      this.gameOverElement.classList.add("visible");

      if (this.finalScore > 0) {
        try {
          const submissionResult = await this.handleScoreSubmission(runStats);
          if (submissionResult && submissionResult.success) {
            if (this.onScoreSubmitted) this.onScoreSubmitted(submissionResult);
          } else if (this.onScoreSubmitFailed) {
            this.onScoreSubmitFailed(
              submissionResult?.message || "Unknown error"
            );
          }
        } catch (error) {
          if (this.onScoreSubmitFailed) {
            this.onScoreSubmitFailed(
              error.message || "Exception during submission"
            );
          }
        }
      } else if (this.onScoreSubmitFailed) {
        this.onScoreSubmitFailed("Score was 0.");
      }
    }, 1000);

    // Reuse the existing game-over analytics signal with a "finish" reason.
    this.analytics.trackGameOver(
      "finish",
      this.distance,
      this.gameTime,
      this.levelGenerator.map.finishSceneName,
      this.finalScore
    );
  }

  /**
   * Is the active map one without a shared leaderboard? That's any player-authored
   * level — the player's own local "custom-" levels AND other players' shared
   * "<username>/<slug>" community levels — whose ids are meaningless to the global
   * board, so they never submit a score.
   */
  isCustomLevel() {
    return isNonLeaderboardMapId(this.levelGenerator?.map?.id || "");
  }

  // New async function to handle score submission flow
  async handleScoreSubmission(runStats = null) {
    // Local custom levels are never submitted to the shared leaderboard.
    if (this.isCustomLevel()) {
      return {
        success: false,
        skipped: true,
        message: "Local level – not submitted to the leaderboard.",
      };
    }

    // REMOVED PROMPT: Get player name from utility
    const playerName = getPlayerName();

    const finalScore = this.finalScore;
    const gameDurationMs = Math.round(this.gameTime); // Ensure gameTime is rounded to ms

    // Use the automatically generated/stored name
    const nameToSubmit = playerName;

    console.log(
      `Attempting to submit score via SupabaseService: ${finalScore}, Duration: ${gameDurationMs}ms, Player: ${
        nameToSubmit || "Anonymous" // Should have a name now
      }`
    );

    // Scope the score to the active map so each map has its own leaderboard.
    // Use the resolved map id so classic/meltdown submit with their real id even
    // when no ?map= was supplied.
    const mapId = this.levelGenerator.map.id;

    try {
      // Assume submitScore handles challenge fetching and returns { success, scoreId, challengeJwt, message }
      // Pass the name, map id, and descriptive end-of-run stats to submitScore.
      const result = await submitScore(
        finalScore,
        gameDurationMs,
        nameToSubmit,
        mapId,
        runStats
      );

      // Return the result object which should contain scoreId and challengeJwt on success
      return result;
    } catch (submissionError) {
      console.error(
        "Unexpected error during submitScore call in Game.js:",
        submissionError
      );
      // Return a failure object consistent with expected structure
      return {
        success: false,
        message: submissionError.message || "Exception during submission",
      };
    }
  }

  updateDisplay() {
    // Update track name display
    this.updateTrackDisplay();

    // Don't update the display if we're waiting for the game to start
    if (this.waitingForStart) {
      return;
    }

    // Update score display
    const scoreElement = document.getElementById("score");
    if (scoreElement) {
      // Get the current maximum unlocked difficulty level
      const difficulty =
        this.levelGenerator.difficultyManager.getMaxUnlockedDifficulty();
      const difficultyName = this.getDifficultyName(difficulty);

      scoreElement.innerHTML = `Distance: ${Math.floor(
        this.distance * 10
      )} m<br />Time: ${this.formatTime(this.gameTime)}<br />${difficultyName}`;
    }
  }

  // Get the display name for a difficulty level
  getDifficultyName(difficulty) {
    switch (difficulty) {
      case "level1":
        return "Easy";
      case "level2":
        return "Medium";
      case "level3":
        return "Hard";
      case "level4":
        return "Brutal 💀";
      case "level5":
        return "Insane 💀💀";
      default:
        return "Easy";
    }
  }

  // Format distance for display (in meters)
  formatDistance(distance) {
    // Convert world units to meters (1 world unit = 10 meters for display)
    const meters = Math.floor(distance * 10);
    return `${meters} m`;
  }

  // Format time for display (MM:SS.MS)
  formatTime(timeMs) {
    // Round to nearest 10ms to match the same precision used in scoring
    const roundedTimeMs = Math.floor(timeMs / 10) * 10;

    const totalSeconds = roundedTimeMs / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.floor((totalSeconds * 100) % 100);

    // Format as MM:SS.MS
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}.${milliseconds.toString().padStart(2, "0")}`;
  }

  updateCoordinates() {
    // Use debug manager to update coordinates display instead
    if (this.debugManager) {
      this.debugManager.updateCoordinates(this.player);
    }
  }

  handleKeyDown(event) {
    // Handle music controls regardless of game state
    if (event.key.toLowerCase() === "m") {
      this.audioManager.toggleMute();
    } else if (event.key.toLowerCase() === "n") {
      this.audioManager.skipTrack();
    }

    // Check if we need to start the game first (any key press starts the game if waiting)
    let startGame = false;
    if (this.waitingForStart && !this.isDead && !this.isPaused) {
      this.start();
      startGame = true;

      // Continue processing the key press for immediate control after starting
      // This is crucial for players holding down run/jump keys while starting
    } else if (!this.isRunning) {
      // If game isn't running and we didn't just start it, don't process controls
      return;
    }

    // Handle game controls
    switch (event.key.toLowerCase()) {
      case " ":
      case "arrowup":
        if (!this.isPaused) {
          this.player.setJumpKeyHeld(true);
          this.player.jump();
        }
        break;
      case "arrowdown":
        if (!this.isPaused) {
          this.player.setLeftKeyHeld(true);
          this.player.updateSpeed();
        }
        break;
      case "arrowright":
      case "d":
        if (!this.isPaused) {
          this.player.setRightKeyHeld(true);
          this.player.updateSpeed();
        }
        break;
      case "arrowleft":
      case "a":
        if (!this.isPaused) {
          this.player.setLeftKeyHeld(true);
          this.player.updateSpeed();
        }
        break;
    }
  }

  handleKeyUp(event) {
    // Only handle key events if the game is running
    if (!this.isRunning) {
      return;
    }

    switch (event.key.toLowerCase()) {
      case " ":
      case "arrowup":
        if (!this.isPaused) {
          this.player.setJumpKeyHeld(false);
        }
        break;
      case "arrowdown":
        if (!this.isPaused) {
          this.player.setLeftKeyHeld(false);
          this.player.updateSpeed();
        }
        break;
      case "arrowright":
      case "d":
        if (!this.isPaused) {
          this.player.setRightKeyHeld(false);
          this.player.updateSpeed();
        }
        break;
      case "arrowleft":
      case "a":
        if (!this.isPaused) {
          this.player.setLeftKeyHeld(false);
          this.player.updateSpeed();
        }
        break;
    }
  }

  toggleMusic() {
    this.audioManager.toggleMute();
  }

  nextTrack() {
    this.audioManager.skipTrack();
  }

  handleResize() {
    // Get the current container dimensions
    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;

    // Calculate the appropriate frustum dimensions to maintain grid cell size
    let frustumHeight, frustumWidth;

    // Calculate container aspect ratio
    const containerAspectRatio = containerWidth / containerHeight;

    // Calculate the base aspect ratio of our standard game view
    const baseAspectRatio = this.baseFrustumWidth / this.baseFrustumHeight;

    // Handle different aspect ratio scenarios
    if (containerAspectRatio < baseAspectRatio) {
      // NARROW VIEWPORT: Set width to base width and scale height proportionally
      frustumWidth = this.baseFrustumWidth;
      frustumHeight = frustumWidth / containerAspectRatio;
    } else {
      // WIDE VIEWPORT: Scale based on container height, with minimum height protection
      // First determine the correct height based on container height
      frustumHeight =
        this.baseFrustumHeight * (containerHeight / this.referenceHeight);

      // Enforce minimum height to never crop vertical content
      frustumHeight = Math.max(frustumHeight, this.baseFrustumHeight);

      // Calculate width to match container aspect ratio
      frustumWidth = frustumHeight * containerAspectRatio;
    }

    this.camera.left = frustumWidth / -2;
    this.camera.right = frustumWidth / 2;
    this.camera.top = frustumHeight - 5;
    this.camera.bottom = -5;

    this.camera.updateProjectionMatrix();

    // Resize renderer to match container size
    this.renderer.setSize(containerWidth, containerHeight);
  }

  update(deltaTime) {
    // If game is neither running nor has blood particles, and player is not dead, do nothing
    if (!this.isRunning && !this.bloodParticleSystem.active && !this.isDead)
      return;

    // Cap raw frame time so a long stall (e.g. a backgrounded tab) can't inject a
    // giant delta. 100ms ≈ 10fps. gameTime (wall-clock-ish, used for scoring) uses
    // this capped value.
    const cappedDeltaTime = Math.min(deltaTime, 100);

    // Normalized simulation step in 60fps-frame units, scaled by gameSpeed, then
    // clamped to MAX_STEP. A single tick never advances more than MAX_STEP frames of
    // physics: on a lag spike the sim briefly slows down instead of teleporting the
    // player (and everything else) forward. This clamp is load-bearing for collision
    // — see docs/architecture.md, "Timestep, max step & tunneling". With MAX_STEP = 2
    // the fastest horizontal travel per tick (0.16 × 2 = 0.32) stays under the
    // narrowest hazard capture window (~0.5), so the discrete (single-sample)
    // collision checks can't be tunneled through. 2.0 also lets a steady 30fps
    // display still run at real time; below that the game slows rather than skips.
    const MAX_STEP = 2.0;
    const deltaGameSpeed = Math.min(
      (cappedDeltaTime / 16.67) * this.gameSpeed, // 16.67ms is ~60fps
      MAX_STEP
    );

    // If waiting for player to start the game, just update player animation
    if (this.waitingForStart) {
      // Still animate player eyes when waiting for start
      if (this.player) {
        this.player.animateEyes(deltaGameSpeed);
      }

      // Also update blood particles if active while waiting to start
      if (this.bloodParticleSystem && this.bloodParticleSystem.active) {
        this.bloodParticleSystem.updateCameraViewBoundaries(
          this.camera,
          this.cameraOffsetX
        );
        this.bloodParticleSystem.update(deltaGameSpeed);
      }

      return;
    }

    // Always update lasers, even when player is dead
    this.frameProfiler.begin("lasers");
    this.laserManager.update(deltaGameSpeed);
    this.frameProfiler.end("lasers");

    // Only update game logic if player is still alive
    if (!this.isDead) {
      // Update level generator to create new scenes as needed
      this.frameProfiler.begin("levelgen");
      this.levelGenerator.update(this.player.position.x);
      this.frameProfiler.end("levelgen");

      // Check if player has fallen below camera view bottom
      // This is our single source of truth for fall death detection
      if (
        this.player.position.y <
        this.camera.position.y + this.camera.bottom - 1
      ) {
        this.gameOver("fall");
        return;
      }

      // Update player physics and animation (includes collision sub-stepping)
      this.frameProfiler.begin("player");
      this.player.update(deltaGameSpeed);
      this.frameProfiler.end("player");

      // Update coordinates display
      this.updateCoordinates();

      // Only update camera if player is moving
      if (!this.player.isColliding && !this.isDead) {
        this.updateCameraPosition();

        // Update distance traveled (player's X position). Clamp to the max reached so a
        // backward wall jump (the only way the player travels left) doesn't tick it down.
        this.distance = Math.max(this.distance, this.player.position.x);

        // Check for distance milestones
        const currentDistanceInMeters = Math.floor(this.distance * 10);
        const currentMilestone =
          Math.floor(currentDistanceInMeters / this.milestoneIncrement) *
          this.milestoneIncrement;

        if (currentMilestone > this.lastMilestone) {
          // Track milestone reached
          this.analytics.trackDistanceMilestone(currentMilestone);
          this.lastMilestone = currentMilestone;
        }
      }
      this.gameTime += cappedDeltaTime;

      // Check for collisions with obstacles, lasers, and projectiles
      this.frameProfiler.begin("hazardChecks");
      const obstacles = this.obstacleManager.getObstacles();
      const obstacleCollision = this.collisionDetector.checkCollisionWithMeshes(
        this.player,
        obstacles
      );

      // Check for collisions with lasers
      const playerBounds = this.player.getBounds();
      const laserCollision = this.laserManager.checkCollision(playerBounds);

      // Check for collision with projectiles
      let projectileCollision = false;
      if (this.projectileManager) {
        projectileCollision = this.collisionDetector.checkProjectileCollision(
          playerBounds,
          this.projectileManager.getProjectiles()
        );
      }
      this.frameProfiler.end("hazardChecks");

      // If player collides with an obstacle, laser, or falls off the screen, trigger game over
      if (
        obstacleCollision ||
        laserCollision ||
        projectileCollision ||
        this.player.position.y < -6 ||
        (obstacleCollision && obstacleCollision.isDeadly)
      ) {
        let deathReason = "fall";
        if (obstacleCollision) deathReason = "obstacle";
        if (laserCollision) deathReason = "laser";
        if (projectileCollision) deathReason = "projectile";
        if (obstacleCollision && obstacleCollision.isDeadly)
          deathReason = "obstacle";

        this.gameOver(deathReason);
        return;
      }

      // Designed (finite) courses end at a finish line: crossing it without
      // having died completes the run. Death checks run first (above), so a
      // lethal hazard on the finish frame still kills. This is a position
      // threshold, not a per-frame accumulator, so it's frame-rate independent
      // (MAX_STEP bounds per-tick travel, so the line can't be skipped).
      if (this.levelGenerator.isDesigned && !this.hasWon) {
        const finishX = this.levelGenerator.getFinishX();
        if (finishX != null && this.player.position.x >= finishX) {
          this.win();
          return;
        }
      }
    }

    // Update blood particles if active, regardless of game state
    this.frameProfiler.begin("particles");
    if (this.bloodParticleSystem) {
      // Update camera view boundaries for the blood particles
      this.bloodParticleSystem.updateCameraViewBoundaries(
        this.camera,
        this.cameraOffsetX
      );
      this.bloodParticleSystem.update(deltaGameSpeed);
      // No longer remove settled particles - keep them until restart
    }
    this.frameProfiler.end("particles");

    // Update projectiles. Pass the clamped sim step so bullet travel is frame-rate
    // independent and shares the MAX_STEP tunneling guard.
    this.frameProfiler.begin("projectiles");
    if (this.projectileManager) {
      this.projectileManager.update(
        deltaGameSpeed,
        this.distance,
        this.camera.position.x,
        this.camera.right - this.camera.left
      );
    }
    this.frameProfiler.end("projectiles");
  }

  // New method to update camera position based on player position and viewport
  updateCameraPosition() {
    // Get current camera frustum width
    const currentFrustumWidth = this.camera.right - this.camera.left;

    // Standard positioning (aligned with player + offset)
    let cameraX = this.player.position.x + this.cameraOffsetX;

    // If frustum width exceeds maximum visible width, we shift camera back
    // This shows more space behind player instead of seeing farther ahead
    if (currentFrustumWidth > this.maxVisibleWidth) {
      // Calculate how much to shift back (half the difference)
      const extraWidth = currentFrustumWidth - this.maxVisibleWidth;
      cameraX -= extraWidth / 2;
    }

    // Position camera horizontally
    this.camera.position.x = cameraX;

    // Keep camera at the same vertical position
    // The vertical space is handled by the frustum height

    // Update the background plane to follow the player directly
    this.backgroundPlane.position.x = this.player.position.x;

    // Update directional light to follow the player
    this.directionalLight.position.x = this.player.position.x + 2;

    // Only update the light target matrix if the player moved significantly
    // This prevents unnecessary matrix calculations every frame
    const minMovementThreshold = 0.05; // Adjust threshold as needed for your game
    const playerDistance = Math.abs(
      this.lastLightTargetX - this.player.position.x
    );

    if (playerDistance > minMovementThreshold) {
      // Update the directional light's shadow camera target to follow the player
      this.directionalLight.target.position.x = this.player.position.x;
      this.directionalLight.target.updateMatrixWorld();
      // Remember the last position where we updated the matrix
      this.lastLightTargetX = this.player.position.x;
    }
  }

  animate(timestamp) {
    // Calculate delta time for smoother animation
    const deltaTime = timestamp - this.lastFrameTime;
    this.lastFrameTime = timestamp;

    // Frame profiler follows the debug overlay toggle; fully no-op when off.
    this.frameProfiler.setEnabled(
      !!(this.debugManager && this.debugManager.enabled)
    );
    this.frameProfiler.beginFrame();

    // Update performance metrics in the tracker
    this.performanceTracker.update(deltaTime);

    // Pass the calculated fps to analytics if needed
    if (this.analytics.frameRateTrackingEnabled) {
      const currentFps = this.performanceTracker.getCurrentFPS();
      if (currentFps > 0) {
        this.analytics.trackFrameRate(currentFps);
      }
    }

    // Update debug performance display
    if (this.debugManager) {
      const perfInfo = this.performanceTracker.getPerformanceInfo();
      this.debugManager.updatePerformance(perfInfo);

      // Store the current FPS for the getFPS helper method
      if (perfInfo && typeof perfInfo.currentFps === "number") {
        this.debugManager.currentFps = perfInfo.currentFps;
      }
    }

    this.updateDisplay();

    // Update camera position even when waiting to start or when player is dead
    if ((this.waitingForStart || this.isDead) && this.player) {
      this.updateCameraPosition();
    }

    // Only update game state if not paused
    if (!this.isPaused) {
      // Update game state
      this.frameProfiler.begin("sim");
      this.update(deltaTime);
      this.frameProfiler.end("sim");
    }

    // Render the scene
    this.frameProfiler.begin("render");
    this.renderer.render(this.scene, this.camera);
    this.frameProfiler.end("render");

    // Close out the frame, then sample GPU/memory stats and push to the overlay.
    this.frameProfiler.endFrame();
    this.frameProfiler.sampleRenderer(this.renderer);
    this.frameProfiler.sampleMemory();
    if (this.debugManager) {
      this.debugManager.updateProfiler(this.frameProfiler.getStats());
    }

    // Request the next frame and store the ID so we can cancel it if needed
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  cleanup() {
    // Stop background music
    this.audioManager.stop();
    this.audioManager.dispose();

    // Cancel animation frame
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Clean up debug manager
    if (this.debugManager) {
      this.debugManager.cleanup();
    }

    // Clean up lasers (dispose frees shared geometry/materials)
    if (this.laserManager) {
      this.laserManager.dispose();
    }

    // Clean up blood particle system (dispose frees shared geometry/materials)
    if (this.bloodParticleSystem) {
      this.bloodParticleSystem.dispose();
    }

    // Clean up projectile manager
    if (this.projectileManager) {
      this.projectileManager.dispose();
    }

    // Remove any event listeners
    window.removeEventListener("resize", this.handleResize.bind(this));

    // Clean up three.js resources
    this.renderer.dispose();

    // Remove the renderer from the DOM
    if (this.renderer.domElement.parentNode) {
      this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
    }

    // Dispose shared geometry/material owned by the entity managers.
    if (this.blockManager) {
      this.blockManager.dispose();
    }
    if (this.obstacleManager) {
      this.obstacleManager.dispose();
    }

    // Clear references
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.player = null;
    this.blockManager = null;
    this.obstacleManager = null;
    this.laserManager = null;
    this.levelGenerator = null;
    this.debugManager = null;
    this.collisionDetector = null;
    this.bloodParticleSystem = null;
  }

  // Debug aid: toggle shadow rendering at runtime to A/B the shadow-pass cost.
  // Watch `draw calls` and `render` in the Frame Profile overlay before/after.
  toggleShadows() {
    if (!this.renderer) return;
    const enabled = !this.renderer.shadowMap.enabled;
    this.renderer.shadowMap.enabled = enabled;
    this.renderer.shadowMap.needsUpdate = true;
    // Materials bake shadow sampling into their shader, so they must recompile
    // for the change to take effect. One-time cost on toggle only.
    this.scene.traverse((obj) => {
      if (obj.material) {
        const materials = Array.isArray(obj.material)
          ? obj.material
          : [obj.material];
        for (const material of materials) material.needsUpdate = true;
      }
    });
    console.log(`[shadows] ${enabled ? "ON" : "OFF"}`);
  }

  // New method to update track display immediately
  updateTrackDisplay() {
    const trackDisplay = document.getElementById("track-display");
    if (trackDisplay) {
      const trackName = this.audioManager.getCurrentTrackName();
      const isMuted = this.audioManager.isMuted;
      const isPlaying = this.audioManager.isPlaying;

      if (trackName && !isMuted && isPlaying) {
        trackDisplay.innerHTML = `♪ ${trackName} ♪`;
        trackDisplay.classList.remove("hidden");
      } else {
        trackDisplay.classList.add("hidden");
      }
    }
  }

  // Add helper to get game duration (needed by main.js for submission initially, now internal)
  getGameDurationMs() {
    return Math.round(this.gameTime);
  }
}
